import React, { useState, useRef, useEffect } from 'react';
import { MediaAsset } from '../../types/common';

interface VideoPlayerProps {
  video: MediaAsset;
  className?: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ video, className = '' }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px'
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleLoadStart = () => {
    setIsLoading(true);
    setHasError(false);
  };

  const handleCanPlay = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  if (hasError) {
    return (
      <div className={`bg-gray-100 rounded-lg flex items-center justify-center ${className}`}>
        <div className="text-center p-8">
          <div className="text-gray-400 text-4xl mb-4">📹</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Video Unavailable</h3>
          <p className="text-gray-600 text-sm">
            The demo video is currently unavailable. Please try again later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative bg-gray-900 rounded-lg overflow-hidden ${className}`}>
      {!isInView && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="text-gray-400 text-4xl mb-4">📹</div>
            <p className="text-gray-600 text-sm">Video will load when visible</p>
          </div>
        </div>
      )}

      {isInView && (
        <>
          {isLoading && (
            <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 text-sm">Loading video...</p>
              </div>
            </div>
          )}
          
          <video
            className="w-full h-full object-cover"
            controls
            preload="metadata"
            onLoadStart={handleLoadStart}
            onCanPlay={handleCanPlay}
            onError={handleError}
            aria-label={video.altText}
          >
            <source src={video.url} type="video/mp4" />
            <track kind="captions" srcLang="en" label="English" />
            <p className="text-white p-4">
              Your browser does not support the video tag.{' '}
              <a href={video.url} className="text-blue-400 hover:text-blue-300 underline">
                Download the video
              </a>
            </p>
          </video>
          
          {video.caption && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
              <p className="text-white text-sm">{video.caption}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VideoPlayer;